import React, { useCallback, useState, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import { dataCatalog } from '../../../../../api/coreData'
import UtilityPanel from './UtilityPanel'
import EditorPanel from './EditorPanel'
import ResultPanel from './ResultPanel'
import ResizableHandle from './ResizablePanels'
import useResizablePanels from './useResizablePanels'

const useStyles = makeStyles(theme => ({
  editorLayout: {
    height: `calc(100vh - ${theme.layout.appBar.height}px)`,
    width: '100vw',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden'
  },
  utilityPanel: {
    flexShrink: 0,
    backgroundColor: 'var(--surface-bg-color-d3)',
    overflow: 'auto',
    borderRight: '1px solid var(--color-neutral-600)'
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  editorPanel: {
    flexShrink: 0,
    borderBottom: '1px solid var(--color-neutral-600)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  resultPanel: {
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  }
}))

function QueryEditor () {
  const classes = useStyles()
  const containerRef = useRef(null)
  const editorRef = useRef(null)
  const { sizes, setUtilityWidth, setEditorHeight } = useResizablePanels()

  const [currentResult, setCurrentResult] = useState(null)
  const { mutateAsync: queryCatalogView, isLoading: queryProcessing } = dataCatalog.useQueryCatalogViewImperative()

  const handleQuery = useCallback(async (queryText) => {
    const result = await queryCatalogView({
      query: {
        dql: queryText
      }
    })

    setCurrentResult(result)
  }, [queryCatalogView, setCurrentResult])

  const handleUtilityResize = useCallback((delta) => {
    const newWidth = Math.max(150, Math.min(500, sizes.utilityWidth + delta))
    setUtilityWidth(newWidth)
  }, [sizes.utilityWidth, setUtilityWidth])

  const handleEditorResize = useCallback((delta) => {
    if (!containerRef.current) return

    const containerHeight = containerRef.current.querySelector(`.${classes.mainContent}`).offsetHeight
    const currentHeight = (sizes.editorHeight / 100) * containerHeight
    const newHeight = currentHeight + delta
    const newPercentage = Math.max(20, Math.min(80, (newHeight / containerHeight) * 100))

    setEditorHeight(newPercentage)
  }, [sizes.editorHeight, setEditorHeight, classes.mainContent])

  const handleUtilityReset = useCallback(() => {
    setUtilityWidth(250)
  }, [setUtilityWidth])

  const handleEditorReset = useCallback(() => {
    setEditorHeight(50)
  }, [setEditorHeight])

  const handleInsertText = useCallback((text) => {
    if (editorRef.current && editorRef.current.insertText) {
      editorRef.current.insertText(text)
    }
  }, [])

  return (
    <div className={classes.editorLayout} ref={containerRef}>
      <div className={classes.utilityPanel} style={{ width: sizes.utilityWidth }}>
        <UtilityPanel onInsertText={handleInsertText} />
      </div>
      <ResizableHandle
        direction='horizontal'
        onResize={handleUtilityResize}
        onDoubleClick={handleUtilityReset}
      />
      <div className={classes.mainContent}>
        <div className={classes.editorPanel} style={{ height: `${sizes.editorHeight}%` }}>
          <EditorPanel ref={editorRef} processing={queryProcessing} onSubmitQuery={handleQuery} />
        </div>
        <ResizableHandle
          direction='vertical'
          onResize={handleEditorResize}
          onDoubleClick={handleEditorReset}
        />
        <div className={classes.resultPanel}>
          <ResultPanel currentResult={currentResult} />
        </div>
      </div>
    </div>
  )
}

export default QueryEditor
